import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Heart, Phone, X, Video, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { toast } from 'sonner';
import SEO from '@/components/SEO';
import Watermark from '@/components/Watermark';
import { useWatermark } from '@/context/WatermarkContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to normalize image URLs
const normalizeImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/api/uploads')) {
    return `${BACKEND_URL}${url}`;
  }
  if (url.startsWith('/uploads')) {
    return `${BACKEND_URL}/api${url}`;
  }
  if (url.includes('/uploads/')) {
    const uploadsPath = url.substring(url.indexOf('/uploads'));
    return `${BACKEND_URL}/api${uploadsPath}`;
  }
  return url;
};

export default function KittenDetail() {
  const { id } = useParams();
  const { priceVisible } = useWatermark();
  const [kitten, setKitten] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showVideoCallModal, setShowVideoCallModal] = useState(false);
  const [depositForm, setDepositForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [videoCallForm, setVideoCallForm] = useState({
    name: '',
    email: '',
    phone: '',
    preferred_date: '',
    preferred_time: '',
    platform: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const apiRef = useRef(API);

  const fetchKitten = useCallback(async () => {
    try {
      const response = await axios.get(`${apiRef.current}/kittens/${id}`);
      setKitten(response.data);
    } catch {
      toast.error('Unable to load kitten details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchKitten();
  }, [fetchKitten]);

  const handleDepositRequest = async (e) => {
    e.preventDefault();
    if (!kitten) return;
    setSubmitting(true);

    try {
      await axios.post(`${apiRef.current}/deposit-request`, {
        ...depositForm,
        kitten_id: kitten.id,
        kitten_name: kitten.name
      });
      toast.success('Reservation request sent! We will contact you shortly to confirm availability.');
      setShowDepositModal(false);
      setDepositForm({ name: '', email: '', phone: '', message: '' });
    } catch {
      toast.error('Failed to send request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVideoCallRequest = async (e) => {
    e.preventDefault();
    if (!kitten) return;
    setSubmitting(true);

    try {
      await axios.post(`${apiRef.current}/video-call-request`, {
        ...videoCallForm,
        kitten_id: kitten.id,
        kitten_name: kitten.name
      });
      toast.success('Video call request sent! We will confirm your appointment shortly.');
      setShowVideoCallModal(false);
      setVideoCallForm({ name: '', email: '', phone: '', preferred_date: '', preferred_time: '', platform: '', message: '' });
    } catch {
      toast.error('Failed to send request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Available':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Reserved':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Sold':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-stone-100 text-stone-800 border-stone-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <SEO title="Loading..." noindex />
        <p className="text-stone-600">Loading...</p>
      </div>
    );
  }

  if (!kitten) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <SEO title="Kitten Not Found" noindex />
        <p className="text-stone-600 mb-4">Kitten not found</p>
        <Link to="/kittens">
          <Button className="bg-[#E85D75] text-white hover:bg-[#D64560] rounded-full">
            <ArrowLeft className="mr-2 w-4 h-4" /> Back to Kittens
          </Button>
        </Link>
      </div>
    );
  }

  const kittenImage = kitten.images && kitten.images.length > 0 ? normalizeImageUrl(kitten.images[0]) : null;

  return (
    <main className="py-20 min-h-screen">
      <SEO 
        title={`${kitten.name} - ${kitten.breed} Kitten | KittenState`}
        description={`Meet ${kitten.name}, a beautiful ${kitten.age} old ${kitten.gender} ${kitten.breed} kitten at KittenState. ${kitten.personality || 'Looking for a loving forever home.'}`}
        keywords={`${kitten.name} kitten, ${kitten.breed}, ${kitten.color} kitten, KittenState, ${kitten.gender} kitten for adoption`}
        image={kittenImage}
        url={`https://kittenstate.com/kitten/${kitten.id}`}
        type="product"
      />
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <Link to="/kittens" className="inline-flex items-center text-stone-600 hover:text-[#E85D75] mb-8 transition-colors">
          <ArrowLeft className="mr-2 w-4 h-4" /> Back to Available Kittens
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div>
            {kitten.images && kitten.images.length > 0 ? (
              <Carousel className="w-full">
                <CarouselContent>
                  {kitten.images.map((image, index) => (
                    <CarouselItem key={`${kitten.id}-image-${index}`}>
                      <div className="aspect-square rounded-2xl overflow-hidden relative">
                        <img
                          src={normalizeImageUrl(image)}
                          alt={`${kitten.name} - Photo ${index + 1}`}
                          className="w-full h-full object-cover"
                          data-testid={`kitten-image-${index}`}
                        />
                        <Watermark size="md" className="bottom-4 right-4" />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                {kitten.images.length > 1 && (
                  <>
                    <CarouselPrevious />
                    <CarouselNext />
                  </>
                )}
              </Carousel>
            ) : (
              <div className="aspect-square rounded-2xl overflow-hidden bg-stone-100">
                <img
                  src="https://images.unsplash.com/photo-1628113268359-034ec254a8e4?w=500"
                  alt={kitten.name}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <div className="flex items-start justify-between mb-4">
              <h1 className="text-5xl md:text-6xl font-serif font-semibold text-stone-800" data-testid="kitten-name">
                {kitten.name}
              </h1>
              <Badge className={`${getStatusColor(kitten.status)} border text-sm`} data-testid="kitten-status">
                {kitten.status}
              </Badge>
            </div>

            {kitten.status !== 'Sold' && priceVisible && (
              <p className="text-3xl font-serif font-semibold text-[#E85D75] mb-8" data-testid="kitten-price">
                ${kitten.price}
              </p>
            )}

            {kitten.status === 'Sold' && (
              <p className="text-xl text-stone-500 mb-8">This kitten has found their forever home!</p>
            )}

            <div className="space-y-6 mb-8">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-stone-50 p-4 rounded-xl">
                  <p className="text-sm text-stone-500 mb-1">Breed</p>
                  <p className="text-lg font-semibold text-stone-800" data-testid="kitten-breed">{kitten.breed}</p>
                </div>
                <div className="bg-stone-50 p-4 rounded-xl">
                  <p className="text-sm text-stone-500 mb-1">Age</p>
                  <p className="text-lg font-semibold text-stone-800" data-testid="kitten-age">{kitten.age}</p>
                </div>
                <div className="bg-stone-50 p-4 rounded-xl">
                  <p className="text-sm text-stone-500 mb-1">Gender</p>
                  <p className="text-lg font-semibold text-stone-800" data-testid="kitten-gender">{kitten.gender}</p>
                </div>
                <div className="bg-stone-50 p-4 rounded-xl">
                  <p className="text-sm text-stone-500 mb-1">Color</p>
                  <p className="text-lg font-semibold text-stone-800" data-testid="kitten-color">{kitten.color}</p>
                </div>
              </div>

              {kitten.personality && (
                <div className="bg-white p-6 rounded-2xl border border-stone-100">
                  <h3 className="text-xl font-serif font-medium text-stone-800 mb-3 flex items-center">
                    <Heart className="w-5 h-5 mr-2 text-[#E85D75]" />
                    Personality
                  </h3>
                  <p className="text-stone-600 leading-relaxed" data-testid="kitten-personality">{kitten.personality}</p>
                </div>
              )}

              {kitten.health_details && (
                <div className="bg-white p-6 rounded-2xl border border-stone-100">
                  <h3 className="text-xl font-serif font-medium text-stone-800 mb-3">Health Details</h3>
                  <p className="text-stone-600 leading-relaxed" data-testid="kitten-health">{kitten.health_details}</p>
                </div>
              )}

              {kitten.vaccination_status && (
                <div className="bg-white p-6 rounded-2xl border border-stone-100">
                  <h3 className="text-xl font-serif font-medium text-stone-800 mb-3">Vaccination Status</h3>
                  <p className="text-stone-600 leading-relaxed" data-testid="kitten-vaccination">
                    {kitten.vaccination_status}
                  </p>
                </div>
              )}
            </div>

            {/* CTA Buttons */}
            {kitten.status === 'Available' && (
              <div className="space-y-3">
                <Button
                  onClick={() => setShowDepositModal(true)}
                  data-testid="reserve-kitten-button"
                  className="w-full bg-[#E85D75] text-white hover:bg-[#D64560] shadow-lg shadow-[#E85D75]/25 rounded-full px-8 py-6 text-lg transition-all hover:scale-[1.02] active:scale-[0.98]"
                >
                  <Heart className="w-5 h-5 mr-2" />
                  Reserve {kitten.name}
                </Button>
                
                <Button
                  onClick={() => setShowVideoCallModal(true)}
                  data-testid="schedule-video-call-button"
                  variant="outline"
                  className="w-full border-2 border-[#E85D75] text-[#E85D75] hover:bg-[#E85D75]/5 rounded-full px-8 py-6 text-lg transition-all"
                >
                  <Video className="w-5 h-5 mr-2" />
                  Schedule Video Call
                </Button>

                <a href="tel:929-333-1936" className="block">
                  <Button
                    data-testid="call-about-kitten-button"
                    variant="ghost"
                    className="w-full text-stone-600 hover:text-[#E85D75] hover:bg-rose-50 rounded-full px-8 py-5 text-base transition-all"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Call: 929-333-1936
                  </Button>
                </a>
              </div>
            )}

            {kitten.status === 'Reserved' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6 text-center">
                <p className="text-yellow-800 font-medium mb-2">This kitten is currently reserved</p>
                <p className="text-sm text-yellow-700">Contact us to be added to the waitlist</p>
                <a href="tel:929-333-1936" className="block mt-4">
                  <Button className="bg-yellow-600 text-white hover:bg-yellow-700 rounded-full">
                    <Phone className="w-4 h-4 mr-2" />
                    Call Us
                  </Button>
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reservation Inquiry Modal */}
      {showDepositModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-stone-100 flex items-center justify-between">
              <h2 className="text-2xl font-serif font-medium text-stone-800">
                Reserve {kitten.name}
              </h2>
              <button
                onClick={() => setShowDepositModal(false)}
                className="text-stone-400 hover:text-stone-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="bg-[#E85D75]/10 rounded-xl p-4 mb-6">
                <p className="text-[#E85D75] font-medium mb-2">No Deposit Required!</p>
                <p className="text-sm text-stone-600 mb-3">
                  You can reserve this kitten without any upfront payment. Pay only when you pick up your kitten.
                </p>
                <p className="text-sm text-stone-600 font-medium">How it works:</p>
                <ol className="text-sm text-stone-600 space-y-1 list-decimal list-inside mt-1">
                  <li>Fill out the form below to express your interest</li>
                  <li>We'll contact you to confirm availability</li>
                  <li>Schedule your pickup and pay when you meet your kitten!</li>
                </ol>
              </div>

              <form onSubmit={handleDepositRequest} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-stone-600 mb-2 block">
                    Your Name *
                  </label>
                  <Input
                    value={depositForm.name}
                    onChange={(e) => setDepositForm({ ...depositForm, name: e.target.value })}
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-stone-600 mb-2 block">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    value={depositForm.email}
                    onChange={(e) => setDepositForm({ ...depositForm, email: e.target.value })}
                    placeholder="your@email.com"
                    required
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-stone-600 mb-2 block">
                    Phone Number *
                  </label>
                  <Input
                    type="tel"
                    value={depositForm.phone}
                    onChange={(e) => setDepositForm({ ...depositForm, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    required
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-stone-600 mb-2 block">
                    Message (Optional)
                  </label>
                  <Textarea
                    value={depositForm.message}
                    onChange={(e) => setDepositForm({ ...depositForm, message: e.target.value })}
                    placeholder="Any questions or when you'd like to visit..."
                    rows={3}
                  />
                </div>

                <div className="pt-4 space-y-3">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#E85D75] text-white hover:bg-[#D64560] rounded-full py-6 text-lg"
                  >
                    {submitting ? 'Sending...' : 'Reserve This Kitten'}
                  </Button>
                  <p className="text-xs text-center text-stone-500">
                    No payment required now — pay only at pickup
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Video Call Request Modal */}
      {showVideoCallModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-stone-100 flex items-center justify-between">
              <h2 className="text-2xl font-serif font-medium text-stone-800">
                Schedule Video Call
              </h2>
              <button
                onClick={() => setShowVideoCallModal(false)}
                className="text-stone-400 hover:text-stone-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="bg-rose-50 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Video className="w-5 h-5 text-[#E85D75] mt-0.5" />
                  <div>
                    <p className="text-[#E85D75] font-medium mb-1">Meet {kitten.name} Live!</p>
                    <p className="text-sm text-stone-600">
                      Schedule a video call to meet {kitten.name} and our family. See the kitten's personality, 
                      ask questions, and get to know us before making your decision.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleVideoCallRequest} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-stone-600 mb-2 block">
                    Your Name *
                  </label>
                  <Input
                    value={videoCallForm.name}
                    onChange={(e) => setVideoCallForm({ ...videoCallForm, name: e.target.value })}
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-stone-600 mb-2 block">
                      Email *
                    </label>
                    <Input
                      type="email"
                      value={videoCallForm.email}
                      onChange={(e) => setVideoCallForm({ ...videoCallForm, email: e.target.value })}
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-stone-600 mb-2 block">
                      Phone *
                    </label>
                    <Input
                      type="tel"
                      value={videoCallForm.phone}
                      onChange={(e) => setVideoCallForm({ ...videoCallForm, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-stone-600 mb-2 block">
                      Preferred Date *
                    </label>
                    <Input
                      type="date"
                      value={videoCallForm.preferred_date}
                      onChange={(e) => setVideoCallForm({ ...videoCallForm, preferred_date: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-stone-600 mb-2 block">
                      Preferred Time *
                    </label>
                    <Select
                      value={videoCallForm.preferred_time}
                      onValueChange={(value) => setVideoCallForm({ ...videoCallForm, preferred_time: value })}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="9:00 AM">9:00 AM</SelectItem>
                        <SelectItem value="10:00 AM">10:00 AM</SelectItem>
                        <SelectItem value="11:00 AM">11:00 AM</SelectItem>
                        <SelectItem value="12:00 PM">12:00 PM</SelectItem>
                        <SelectItem value="1:00 PM">1:00 PM</SelectItem>
                        <SelectItem value="2:00 PM">2:00 PM</SelectItem>
                        <SelectItem value="3:00 PM">3:00 PM</SelectItem>
                        <SelectItem value="4:00 PM">4:00 PM</SelectItem>
                        <SelectItem value="5:00 PM">5:00 PM</SelectItem>
                        <SelectItem value="6:00 PM">6:00 PM</SelectItem>
                        <SelectItem value="7:00 PM">7:00 PM</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-stone-600 mb-2 block">
                    Video Platform *
                  </label>
                  <Select
                    value={videoCallForm.platform}
                    onValueChange={(value) => setVideoCallForm({ ...videoCallForm, platform: value })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select platform" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FaceTime">FaceTime</SelectItem>
                      <SelectItem value="Zoom">Zoom</SelectItem>
                      <SelectItem value="WhatsApp">WhatsApp Video</SelectItem>
                      <SelectItem value="Google Meet">Google Meet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-stone-600 mb-2 block">
                    Questions or Notes (Optional)
                  </label>
                  <Textarea
                    value={videoCallForm.message}
                    onChange={(e) => setVideoCallForm({ ...videoCallForm, message: e.target.value })}
                    placeholder="Anything you'd like to know or discuss..."
                    rows={3}
                  />
                </div>

                <div className="pt-4">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#E85D75] text-white hover:bg-[#D64560] rounded-full py-6 text-lg"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    {submitting ? 'Sending...' : 'Request Video Call'}
                  </Button>
                  <p className="text-xs text-center text-stone-500 mt-3">
                    We'll confirm your appointment within 24 hours
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
